import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { AiSparkleIcon } from '@/components/icons/AiSparkleIcon'
import {
    Alert,
    Drawer,
    Tooltip,
    Button,
    Checkbox,
    Input,
    message,
    Modal,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
} from 'antd'
import { CheckCircleFilled, UploadOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/es/table'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { ContentPath } from 'common/src/ContentPath'
import { ContentTree } from 'common/src/ContentTree/types'
import {
    AgentContentJob,
    AgentContentJobMode,
    AgentContentJobStatus,
} from 'common/src/api/contentFrontendVsContentBackend/agentContent'
import { AnthropicModelName } from 'common/src/api/contentFrontendVsContentBackend/llm'
import {
    MappingConfidence,
    MappingFlagType,
    PreviousExamFileSetData,
    PreviousExamFileSlot,
    PreviousExamIdentifier,
    PreviousExamMappingDecisionData,
    PreviousExamAuthoredProblem,
} from 'common/src/api/contentFrontendVsContentBackend/previousExamAuthoring'
import {
    OriginalExamAbbreviation,
    SittingSeason,
} from 'common/src/previousExamAnalysis/types'
import {
    getAgentContentJob,
    getPreviousExamFileSet,
    listPreviousExamFileSets,
    listAgentContentJobsForCourse,
    listPreviousExamMappingDecisions,
    listPreviousExamProblems,
    resolvePreviousExamMappingDecision,
    startAgentContentJob,
    uploadPreviousExamFile,
} from '@/api/cb/agentContent'
import { agentActivityAtom } from '@/atoms/agentActivity'
import {
    frontendDisplayedCourseSelector,
    isActiveCourseReadOnlySelector,
} from '@/atoms/auth'
import { contentTreeStateAtom } from '@/atoms/contentTree'
import { viewingStateAtom, ViewingStateType } from '@/atoms/viewingState'
import { getAgentModelLabel } from '@/components/GlobalAgentActivity/agentActivityLabels'
import { syncErrorMessage, syncSuccessMessage } from '@/utils/syncMessages'
import styles from './PreviousExamsManager.module.less'

const currentYear = new Date().getFullYear()
const YEARS = Array.from(
    { length: currentYear - 2012 + 1 },
    (_, index) => currentYear - index
)
const MODELS = [
    AnthropicModelName['claude-sonnet-5-5'],
    AnthropicModelName['claude-opus-5-5'],
    AnthropicModelName['claude-fable-5'],
]
const DEFAULT_MODEL = AnthropicModelName['claude-opus-5-5']
const SLOT_CONFIG: {
    slot: PreviousExamFileSlot
    label: string
    extension: string
}[] = [
    {
        slot: PreviousExamFileSlot.questionPdf,
        label: 'Question paper PDF',
        extension: 'pdf',
    },
    {
        slot: PreviousExamFileSlot.solutionPdf,
        label: 'Solution paper PDF',
        extension: 'pdf',
    },
    {
        slot: PreviousExamFileSlot.questionXlsx,
        label: 'Question workbook XLSX',
        extension: 'xlsx',
    },
    {
        slot: PreviousExamFileSlot.solutionXlsx,
        label: 'Solution workbook XLSX',
        extension: 'xlsx',
    },
]
const isActive = (status: AgentContentJobStatus): boolean =>
    status === AgentContentJobStatus.queued ||
    status === AgentContentJobStatus.waitingForLock ||
    status === AgentContentJobStatus.running
const sameExam = (
    left: PreviousExamIdentifier | null | undefined,
    right: PreviousExamIdentifier
): boolean =>
    !!left &&
    left.year === right.year &&
    left.sittingSeason === right.sittingSeason &&
    left.originalExamAbbreviation === right.originalExamAbbreviation
const encodeExamId = (exam: PreviousExamIdentifier): string =>
    `${exam.year}_${exam.sittingSeason}_${exam.originalExamAbbreviation}`
const decodeExamId = (id: string | null): PreviousExamIdentifier | null => {
    if (!id) return null
    const [yearText, season, ...abbreviationParts] = id.split('_')
    const year = Number(yearText)
    const abbreviation = abbreviationParts.join('_') as OriginalExamAbbreviation
    if (
        !Number.isInteger(year) ||
        !Object.values(SittingSeason).includes(season as SittingSeason) ||
        !Object.values(OriginalExamAbbreviation).includes(abbreviation)
    )
        return null
    return {
        year,
        sittingSeason: season as SittingSeason,
        originalExamAbbreviation: abbreviation,
    }
}
const formatSize = (bytes: number): string =>
    bytes < 1024 * 1024
        ? `${(bytes / 1024).toFixed(1)} KB`
        : `${(bytes / (1024 * 1024)).toFixed(1)} MB`
const formatRelativeTime = (timestamp: string): string => {
    const minutes = Math.max(
        0,
        Math.round((Date.now() - new Date(timestamp).getTime()) / 60_000)
    )
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.round(minutes / 60)
    return hours < 24 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`
}
const humanPath = (tree: ContentTree, path: ContentPath): string => {
    const titles: string[] = []
    let node = tree
    for (const id of path) {
        const child = node.children.find((candidate) => candidate.id === id)
        if (!child) return path.join(' › ')
        titles.push(child.name)
        node = child
    }
    return titles.join(' › ')
}
const leafOptions = (tree: ContentTree): { value: string; label: string }[] => {
    const result: { value: string; label: string }[] = []
    const walk = (node: ContentTree, path: ContentPath): void => {
        if (!node.children.length && path.length)
            result.push({
                value: JSON.stringify(path),
                label: humanPath(tree, path),
            })
        else node.children.forEach((child) => walk(child, [...path, child.id]))
    }
    walk(tree, [])
    return result
}

export const PreviousExamsManager: React.FC = (): ReactElement => {
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const isReadOnly = useRecoilValue(isActiveCourseReadOnlySelector)
    const activity = useRecoilValue(agentActivityAtom)
    const contentTreeState = useRecoilValue(contentTreeStateAtom)
    const [viewingState, setViewingState] = useRecoilState(viewingStateAtom)
    const openViewingState = useSetRecoilState(viewingStateAtom)
    const initialExam = decodeExamId(viewingState.id) ?? {
        year: currentYear,
        sittingSeason: SittingSeason.fall,
        originalExamAbbreviation: OriginalExamAbbreviation.GHADV,
    }
    const [exam, setExam] = useState<PreviousExamIdentifier>(initialExam)
    const [fileSet, setFileSet] = useState<PreviousExamFileSetData | null>(null)
    const [allFileSets, setAllFileSets] = useState<PreviousExamFileSetData[]>(
        []
    )
    const [allRuns, setAllRuns] = useState<AgentContentJob[]>([])
    const [allExamDecisions, setAllExamDecisions] = useState<
        PreviousExamMappingDecisionData[]
    >([])
    const [problems, setProblems] = useState<PreviousExamAuthoredProblem[]>([])
    const [loadingProblems, setLoadingProblems] = useState(false)
    const [loadingFiles, setLoadingFiles] = useState(false)
    const [uploadingSlot, setUploadingSlot] =
        useState<PreviousExamFileSlot | null>(null)
    const fileInputs = useRef<
        Partial<Record<PreviousExamFileSlot, HTMLInputElement | null>>
    >({})
    const [request, setRequest] = useState('')
    const [model, setModel] = useState<AnthropicModelName>(() => {
        const saved = localStorage.getItem(
            `previousExams_model_${courseName}`
        ) as AnthropicModelName | null
        return saved && MODELS.includes(saved) ? saved : DEFAULT_MODEL
    })
    const [starting, setStarting] = useState(false)
    const [startedJobId, setStartedJobId] = useState<string | null>(null)
    const [liveJob, setLiveJob] = useState<AgentContentJob | null>(null)
    const [history, setHistory] = useState<AgentContentJob[]>([])
    const [unresolvedOnly, setUnresolvedOnly] = useState(true)
    const [decisions, setDecisions] = useState<
        PreviousExamMappingDecisionData[]
    >([])
    const [loadingDecisions, setLoadingDecisions] = useState(false)
    const [resolutions, setResolutions] = useState<
        Record<string, { path: string; keepNote: boolean }>
    >({})
    const [resolvingId, setResolvingId] = useState<string | null>(null)
    const [detailDecisionId, setDetailDecisionId] = useState<string | null>(
        null
    )
    const options = useMemo(
        () => leafOptions(contentTreeState.contentTree),
        [contentTreeState.contentTree]
    )
    // Per-exam readiness for the CURRENTLY selected year+season: file count
    // out of four, and whether a succeeded authoring run exists
    const examStatus = useCallback(
        (
            abbreviation: OriginalExamAbbreviation
        ): { files: number; ran: boolean } => {
            const candidate = {
                ...exam,
                originalExamAbbreviation: abbreviation,
            }
            const set = allFileSets.find((item) =>
                sameExam(item.examIdentifier, candidate)
            )
            const files = set
                ? Object.values(set.files).filter(Boolean).length
                : 0
            const ran = allRuns.some(
                (job) =>
                    sameExam(
                        job.previousExamAuthoring?.examIdentifier,
                        candidate
                    ) && job.status === AgentContentJobStatus.succeeded
            )
            return { files, ran }
        },
        [allFileSets, allRuns, exam]
    )
    const abbreviationOptions = useMemo(
        () =>
            Object.values(OriginalExamAbbreviation).map((value) => {
                const { files, ran } = examStatus(value)
                const suffix = ran
                    ? ` — ${files}/4 files · run ✓`
                    : files
                      ? ` — ${files}/4 files`
                      : ''
                return { value, label: `${value}${suffix}` }
            }),
        [examStatus]
    )
    const openRun = useCallback(
        (jobId: string) =>
            openViewingState((state) => ({
                ...state,
                viewingStateType: ViewingStateType.agentActivity,
                id: jobId,
            })),
        [openViewingState]
    )

    useEffect(() => {
        if (viewingState.viewingStateType !== ViewingStateType.previousExams)
            return
        const encoded = encodeExamId(exam)
        if (viewingState.id !== encoded)
            setViewingState((state) => ({ ...state, id: encoded }))
    }, [exam, setViewingState, viewingState.id, viewingState.viewingStateType])
    useEffect(() => {
        if (viewingState.viewingStateType !== ViewingStateType.previousExams)
            return
        const decoded = decodeExamId(viewingState.id)
        if (decoded && !sameExam(decoded, exam)) setExam(decoded)
    }, [viewingState.id, viewingState.viewingStateType])
    useEffect(
        () => localStorage.setItem(`previousExams_model_${courseName}`, model),
        [courseName, model]
    )

    const refreshFiles = useCallback(async () => {
        setLoadingFiles(true)
        try {
            const response = await getPreviousExamFileSet({
                courseName,
                examIdentifier: exam,
            })
            if (response.data.success) setFileSet(response.data.payload.fileSet)
            else syncErrorMessage(response.data.error)
            const allResponse = await listPreviousExamFileSets({ courseName })
            if (allResponse.data.success)
                setAllFileSets(allResponse.data.payload.fileSets)
        } finally {
            setLoadingFiles(false)
        }
    }, [courseName, exam])
    const refreshHistory = useCallback(async () => {
        const response = await listAgentContentJobsForCourse({ courseName })
        if (!response.data.success) return
        const modeJobs = response.data.payload.jobs.filter(
            (job) => job.mode === AgentContentJobMode.previousExamAuthoring
        )
        setAllRuns(modeJobs)
        setHistory(
            modeJobs.filter((job) =>
                sameExam(job.previousExamAuthoring?.examIdentifier, exam)
            )
        )
    }, [courseName, exam])
    const refreshDecisions = useCallback(async () => {
        setLoadingDecisions(true)
        try {
            const response = await listPreviousExamMappingDecisions({
                courseName,
                examIdentifier: exam,
                unresolvedOnly,
            })
            if (response.data.success)
                setDecisions(response.data.payload.decisions)
            else syncErrorMessage(response.data.error)
        } finally {
            setLoadingDecisions(false)
        }
    }, [courseName, exam, unresolvedOnly])
    const refreshProblems = useCallback(async () => {
        setLoadingProblems(true)
        try {
            const response = await listPreviousExamProblems({
                courseName,
                examIdentifier: exam,
            })
            if (response.data.success)
                setProblems(response.data.payload.problems)
            const decisionsResponse = await listPreviousExamMappingDecisions({
                courseName,
                examIdentifier: exam,
                unresolvedOnly: false,
            })
            if (decisionsResponse.data.success)
                setAllExamDecisions(decisionsResponse.data.payload.decisions)
        } finally {
            setLoadingProblems(false)
        }
    }, [courseName, exam])
    useEffect(() => void refreshProblems(), [refreshProblems])
    useEffect(() => void refreshFiles(), [refreshFiles])
    useEffect(() => void refreshHistory(), [refreshHistory])
    useEffect(() => void refreshDecisions(), [refreshDecisions])

    const activeJob =
        activity?.activeJobs.find(
            (job) =>
                job.mode === AgentContentJobMode.previousExamAuthoring &&
                job.courseName === courseName &&
                isActive(job.status)
        ) ?? null
    const displayedJobId = activeJob?.jobId ?? startedJobId
    useEffect(() => {
        if (!displayedJobId) {
            setLiveJob(null)
            return
        }
        let mounted = true
        const tick = async (): Promise<void> => {
            const response = await getAgentContentJob({ jobId: displayedJobId })
            if (!mounted || !response.data.success) return
            setLiveJob(response.data.payload.job)
            if (!isActive(response.data.payload.job.status)) {
                setStartedJobId(null)
                void refreshHistory()
                void refreshDecisions()
                void refreshProblems()
            }
        }
        void tick()
        const interval = setInterval(() => void tick(), 5_000)
        return () => {
            mounted = false
            clearInterval(interval)
        }
    }, [displayedJobId, refreshDecisions, refreshHistory, refreshProblems])

    const doUpload = async (
        slot: PreviousExamFileSlot,
        extension: string,
        file: File
    ): Promise<void> => {
        if (file.name.split('.').pop()?.toLowerCase() !== extension) {
            syncErrorMessage(`This slot requires a .${extension} file.`)
            return
        }
        setUploadingSlot(slot)
        try {
            const response = await uploadPreviousExamFile(
                courseName,
                exam,
                slot,
                file
            )
            if (response.data.success) {
                setFileSet(response.data.payload.fileSet)
                syncSuccessMessage('Previous exam file uploaded.')
            } else syncErrorMessage(response.data.error)
        } finally {
            setUploadingSlot(null)
        }
    }
    const chooseUpload = (slot: PreviousExamFileSlot): void => {
        if (fileSet?.files[slot])
            Modal.confirm({
                title: 'Replace stored file?',
                content:
                    'Re-uploading clears the cached transcription for this file.',
                okText: 'Replace',
                okButtonProps: { danger: true },
                onOk: () => fileInputs.current[slot]?.click(),
            })
        else fileInputs.current[slot]?.click()
    }
    const start = async (): Promise<void> => {
        setStarting(true)
        try {
            const response = await startAgentContentJob({
                courseName,
                contentPath: [],
                mode: AgentContentJobMode.previousExamAuthoring,
                modelName: model,
                instructorRequest: request.trim() || undefined,
                previousExamAuthoring: { examIdentifier: exam },
            })
            if (!response.data.success) {
                syncErrorMessage(
                    `Failed to start previous exam run: ${response.data.error}`
                )
                return
            }
            setStartedJobId(response.data.payload.jobId)
            message.success({
                duration: 8,
                content: (
                    <span>
                        {response.data.payload.waitingOnJobId
                            ? 'Run queued behind another job — it starts automatically when that run finishes. '
                            : 'Previous exam authoring started. '}
                        <Button
                            type="link"
                            size="small"
                            onClick={() => openRun(response.data.payload.jobId)}
                        >
                            View run
                        </Button>
                    </span>
                ),
            })
        } finally {
            setStarting(false)
        }
    }
    const resolve = async (
        decision: PreviousExamMappingDecisionData
    ): Promise<void> => {
        const draft = resolutions[decision.id] ?? {
            path: JSON.stringify(decision.agentProposal.contentPath),
            keepNote: true,
        }
        setResolvingId(decision.id)
        try {
            const response = await resolvePreviousExamMappingDecision({
                courseName,
                decisionId: decision.id,
                contentPath: JSON.parse(draft.path) as ContentPath,
                acceptedGraderNote: draft.keepNote,
            })
            if (response.data.success) {
                syncSuccessMessage('Mapping decision resolved.')
                setDetailDecisionId(null)
                await refreshDecisions()
                void refreshProblems()
            } else syncErrorMessage(response.data.error)
        } finally {
            setResolvingId(null)
        }
    }
    const truncate = (text: string, max = 90): string =>
        text.length > max ? `${text.slice(0, max)}…` : text
    const detailDecision =
        decisions.find((decision) => decision.id === detailDecisionId) ?? null
    const columns: ColumnsType<PreviousExamMappingDecisionData> = [
        {
            title: 'Question',
            width: 130,
            render: (_, row) =>
                `${row.examIdentifier.originalExamAbbreviation} Q${row.questionNumber}${row.subParts.length ? ` (${row.subParts.join(', ')})` : ''}`,
        },
        {
            title: "Agent's section",
            width: 220,
            render: (_, row) => (
                <Tooltip
                    title={humanPath(
                        contentTreeState.contentTree,
                        row.agentProposal.contentPath
                    )}
                >
                    {truncate(
                        humanPath(
                            contentTreeState.contentTree,
                            row.agentProposal.contentPath
                        ),
                        60
                    )}
                </Tooltip>
            ),
        },
        {
            title: 'Confidence',
            width: 100,
            render: (_, row) => (
                <Tag
                    color={
                        {
                            [MappingConfidence.high]: 'green',
                            [MappingConfidence.medium]: 'orange',
                            [MappingConfidence.low]: 'red',
                        }[row.agentProposal.confidence]
                    }
                >
                    {row.agentProposal.confidence}
                </Tag>
            ),
        },
        {
            title: 'Why flagged',
            render: (_, row) => {
                const parts = []
                if (row.agentProposal.flagReason)
                    parts.push(row.agentProposal.flagReason)
                if (row.agentProposal.graderNote)
                    parts.push(
                        `Grader note (${row.agentProposal.graderNote.severity}): ${row.agentProposal.graderNote.commentary}`
                    )
                if (!parts.length) return '—'
                const joined = parts.join(' · ')
                return (
                    <Tooltip title={joined} overlayStyle={{ maxWidth: 560 }}>
                        <span>
                            <Tag>
                                {row.agentProposal.flagType ===
                                MappingFlagType.content
                                    ? 'content'
                                    : 'mapping'}
                            </Tag>
                            {row.agentProposal.graderNote && (
                                <Tag
                                    color={
                                        row.agentProposal.graderNote
                                            .severity === 'material'
                                            ? 'red'
                                            : 'gold'
                                    }
                                >
                                    grader note
                                </Tag>
                            )}
                            {truncate(joined)}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            title: 'Status',
            width: 200,
            render: (_, row) =>
                row.finalDecision ? (
                    <Tooltip
                        title={`${humanPath(contentTreeState.contentTree, row.finalDecision.contentPath)} · ${row.finalDecision.acceptedGraderNote ? 'note kept' : 'note removed'} · ${row.finalDecision.decidedByDisplayName ?? 'unknown'}`}
                    >
                        <Tag>ruled</Tag>
                    </Tooltip>
                ) : (
                    <Button
                        size={'small'}
                        type={'primary'}
                        onClick={() => setDetailDecisionId(row.id)}
                    >
                        Review
                    </Button>
                ),
        },
    ]
    const flagsByProblemId = useMemo(() => {
        const map = new Map<
            string,
            { confidence: MappingConfidence; resolved: boolean }
        >()
        for (const decision of allExamDecisions)
            if (decision.practiceProblemId)
                map.set(decision.practiceProblemId, {
                    confidence: decision.agentProposal.confidence,
                    resolved: !!decision.finalDecision,
                })
        return map
    }, [allExamDecisions])
    const problemColumns: ColumnsType<PreviousExamAuthoredProblem> = [
        {
            title: 'Question',
            width: 110,
            render: (_, row) =>
                `Q${row.questionNumber}${row.subPartLetter ?? ''}${row.nestedSubPartLetter ? `(${row.nestedSubPartLetter})` : ''}`,
        },
        {
            title: 'Section',
            render: (_, row) => row.contentPathTitles.join(' › '),
        },
        { title: 'Topic', render: (_, row) => row.topic ?? '—' },
        {
            title: 'Type',
            width: 130,
            render: (_, row) => (
                <>
                    {row.isMathematical && <Tag color={'blue'}>math</Tag>}
                    {row.isSynthesis && <Tag color={'purple'}>synthesis</Tag>}
                    {!row.isMathematical && !row.isSynthesis && (
                        <Tag>qualitative</Tag>
                    )}
                </>
            ),
        },
        {
            title: 'Points',
            width: 70,
            render: (_, row) => row.points ?? '—',
        },
        {
            title: 'Confidence',
            width: 120,
            render: (_, row) => {
                const flag = flagsByProblemId.get(row.practiceProblemId)
                // No decision row = the agent didn't flag it (or the problem
                // predates the agent) — claim nothing beyond that
                if (!flag) return <Tag>not flagged</Tag>
                return (
                    <Tag color={flag.resolved ? 'default' : 'gold'}>
                        {flag.confidence}
                        {flag.resolved ? ' · ruled' : ' · needs review'}
                    </Tag>
                )
            },
        },
        {
            title: '',
            width: 110,
            render: (_, row) => (
                <Button
                    type={'link'}
                    size={'small'}
                    onClick={() =>
                        openViewingState({
                            contentPath: row.contentPath,
                            viewingStateType: ViewingStateType.practiceProblems,
                            id: row.practiceProblemId,
                        })
                    }
                >
                    Open problem
                </Button>
            ),
        },
    ]
    const runningStatus =
        liveJob?.status ?? activeJob?.status ?? AgentContentJobStatus.queued
    const latestProgress =
        liveJob?.progressMessages[liveJob.progressMessages.length - 1]
    const latestJob = history[0]

    return (
        <div className={styles.manager}>
            <Typography.Title level={3}>Previous Exams</Typography.Title>
            <Space wrap>
                <Select
                    showSearch
                    optionFilterProp={'label'}
                    value={exam.year}
                    options={YEARS.map((year) => ({
                        value: year,
                        label: String(year),
                    }))}
                    onChange={(year) =>
                        setExam((value) => ({ ...value, year }))
                    }
                />
                <Select
                    showSearch
                    optionFilterProp={'label'}
                    className={styles.seasonSelect}
                    value={exam.sittingSeason}
                    options={Object.values(SittingSeason).map((value) => ({
                        value,
                        label: value,
                    }))}
                    onChange={(sittingSeason) =>
                        setExam((value) => ({ ...value, sittingSeason }))
                    }
                />
                <Select
                    showSearch
                    optionFilterProp={'label'}
                    className={styles.abbreviationSelect}
                    value={exam.originalExamAbbreviation}
                    options={abbreviationOptions}
                    onChange={(originalExamAbbreviation) =>
                        setExam((value) => ({
                            ...value,
                            originalExamAbbreviation,
                        }))
                    }
                />
            </Space>
            <Typography.Title level={4}>File set</Typography.Title>
            {loadingFiles ? (
                <Spin />
            ) : (
                <div className={styles.slots}>
                    {SLOT_CONFIG.map(({ slot, label, extension }) => {
                        const stored = fileSet?.files[slot]
                        return (
                            <div
                                className={`${styles.slot} ${stored ? styles.slotFilled : styles.slotMissing}`}
                                key={slot}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={(event) => {
                                    event.preventDefault()
                                    const file = event.dataTransfer.files[0]
                                    if (file)
                                        void doUpload(slot, extension, file)
                                }}
                                onClick={() =>
                                    !isReadOnly && chooseUpload(slot)
                                }
                            >
                                <input
                                    hidden
                                    ref={(element) => {
                                        fileInputs.current[slot] = element
                                    }}
                                    type="file"
                                    accept={`.${extension}`}
                                    onChange={(event) => {
                                        const file = event.target.files?.[0]
                                        if (file)
                                            void doUpload(slot, extension, file)
                                        event.target.value = ''
                                    }}
                                />
                                {uploadingSlot === slot ? (
                                    <Spin size="small" />
                                ) : stored ? (
                                    <CheckCircleFilled
                                        className={styles.slotCheck}
                                    />
                                ) : (
                                    <UploadOutlined />
                                )}
                                <strong>{label}</strong>
                                {stored ? (
                                    <>
                                        <Tag
                                            className={styles.slotTag}
                                            color={'green'}
                                        >
                                            Uploaded
                                        </Tag>
                                        <span>
                                            {stored.fileName} ·{' '}
                                            {formatSize(stored.bytes)} ·{' '}
                                            {new Date(
                                                stored.uploadedAt
                                            ).toLocaleString()}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <Tag
                                            className={styles.slotTag}
                                            color={'orange'}
                                        >
                                            Missing
                                        </Tag>
                                        <span>
                                            Drop or click to upload .{extension}
                                        </span>
                                    </>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
            <Alert
                showIcon
                type={fileSet?.complete ? 'success' : 'warning'}
                message={
                    fileSet?.complete
                        ? 'File set complete — runs enabled'
                        : 'Upload all four files to enable runs'
                }
            />
            <Typography.Title level={4}>Run</Typography.Title>
            {displayedJobId && (
                <Alert
                    className={styles.banner}
                    showIcon
                    type="info"
                    message={
                        <span>
                            Agent run{' '}
                            {runningStatus ===
                            AgentContentJobStatus.waitingForLock
                                ? 'waiting for another run to finish'
                                : runningStatus === AgentContentJobStatus.queued
                                  ? 'queued — waiting for an agent slot'
                                  : 'running'}
                            {latestProgress ? ` · ${latestProgress}` : ''}{' '}
                            <Button
                                type="link"
                                size="small"
                                onClick={() => openRun(displayedJobId)}
                            >
                                View run
                            </Button>
                        </span>
                    }
                />
            )}
            {!displayedJobId && latestJob && (
                <Alert
                    className={styles.banner}
                    showIcon
                    type={
                        latestJob.status === AgentContentJobStatus.succeeded
                            ? 'success'
                            : 'warning'
                    }
                    message={
                        <span>
                            {`Last agent run: ${latestJob.status} · ${getAgentModelLabel(latestJob.modelName)} · by ${latestJob.requestedByDisplayName ?? 'unknown'} · ${formatRelativeTime(latestJob.createdAt)} `}
                            <Button
                                type="link"
                                size="small"
                                onClick={() => openRun(latestJob.id)}
                            >
                                View run
                            </Button>
                        </span>
                    }
                />
            )}
            <fieldset
                disabled={isReadOnly || !fileSet?.complete || !!displayedJobId}
                className={styles.fieldset}
            >
                <Input.TextArea
                    rows={3}
                    value={request}
                    placeholder={
                        'Optional guidance — e.g. “author questions 1-6” or “revisit question 3’s mapping”'
                    }
                    onChange={(event) => setRequest(event.target.value)}
                />
                <Space>
                    <Select
                        className={styles.modelSelect}
                        value={model}
                        options={MODELS.map((value) => ({
                            value,
                            label: getAgentModelLabel(value),
                        }))}
                        onChange={setModel}
                    />
                    <Button
                        type="primary"
                        loading={starting}
                        onClick={() => void start()}
                    >
                        <AiSparkleIcon /> Start
                    </Button>
                </Space>
            </fieldset>
            <Typography.Title level={4}>
                Authored problems ({problems.length})
            </Typography.Title>
            <Typography.Text className={styles.reviewHint} type={'secondary'}>
                Every problem tied to this exam, wherever its syllabus mapping
                placed it — multi-part questions are split across sections on
                purpose.
            </Typography.Text>
            <Table
                className={styles.table}
                size="small"
                rowKey="practiceProblemId"
                loading={loadingProblems}
                columns={problemColumns}
                dataSource={problems}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 900 }}
            />
            <Typography.Title level={4}>Review</Typography.Title>
            <Checkbox
                checked={unresolvedOnly}
                onChange={(event) => setUnresolvedOnly(event.target.checked)}
            >
                Unresolved only
            </Checkbox>
            <Typography.Text className={styles.reviewHint} type={'secondary'}>
                The agent flags items it was unsure about — a section mapping it
                lacked confidence in, or a grader-mistake note it added to a
                solution. Checked: only items still awaiting your ruling.
                Unchecked: also show items you have already ruled on.
            </Typography.Text>
            <Table
                className={`${styles.table} ${styles.clickableRows}`}
                size="small"
                rowKey="id"
                loading={loadingDecisions}
                columns={columns}
                dataSource={decisions}
                scroll={{ x: 1100 }}
                pagination={{ pageSize: 10 }}
                onRow={(row) => ({
                    onClick: () => setDetailDecisionId(row.id),
                })}
            />
            <Drawer
                open={!!detailDecision}
                onClose={() => setDetailDecisionId(null)}
                width={620}
                destroyOnClose
                title={
                    detailDecision
                        ? `${detailDecision.examIdentifier.originalExamAbbreviation} ${detailDecision.examIdentifier.year} ${detailDecision.examIdentifier.sittingSeason} — Q${detailDecision.questionNumber}${detailDecision.subParts.length ? ` (${detailDecision.subParts.join(', ')})` : ''}`
                        : ''
                }
            >
                {detailDecision && (
                    <Space
                        direction={'vertical'}
                        size={16}
                        className={styles.detailBody}
                    >
                        <div>
                            <Typography.Text type={'secondary'}>
                                Agent's section (confidence:{' '}
                                {detailDecision.agentProposal.confidence})
                            </Typography.Text>
                            <div>
                                {humanPath(
                                    contentTreeState.contentTree,
                                    detailDecision.agentProposal.contentPath
                                )}
                            </div>
                        </div>
                        {detailDecision.agentProposal.flagReason && (
                            <div>
                                <Typography.Text type={'secondary'}>
                                    Why the agent flagged this
                                </Typography.Text>
                                <Typography.Paragraph>
                                    {detailDecision.agentProposal.flagReason}
                                </Typography.Paragraph>
                            </div>
                        )}
                        {detailDecision.agentProposal.graderNote && (
                            <Alert
                                type={
                                    detailDecision.agentProposal.graderNote
                                        .severity === 'material'
                                        ? 'error'
                                        : 'warning'
                                }
                                showIcon
                                message={`Grader-mistake note (${detailDecision.agentProposal.graderNote.severity}) — student-visible unless removed`}
                                description={
                                    detailDecision.agentProposal.graderNote
                                        .commentary
                                }
                            />
                        )}
                        {detailDecision.practiceProblemId && (
                            <Button
                                onClick={() =>
                                    openViewingState({
                                        contentPath:
                                            detailDecision.agentProposal
                                                .contentPath,
                                        viewingStateType:
                                            ViewingStateType.practiceProblems,
                                        id: detailDecision.practiceProblemId,
                                    })
                                }
                            >
                                Open the problem
                            </Button>
                        )}
                        {detailDecision.finalDecision ? (
                            <Alert
                                type={'success'}
                                showIcon
                                message={'Ruled'}
                                description={`${humanPath(contentTreeState.contentTree, detailDecision.finalDecision.contentPath)} · ${detailDecision.finalDecision.acceptedGraderNote ? 'note kept' : 'note removed'} · ${detailDecision.finalDecision.decidedByDisplayName ?? 'unknown'}`}
                            />
                        ) : (
                            <Space
                                direction={'vertical'}
                                className={styles.resolver}
                            >
                                {detailDecision.agentProposal.flagType ===
                                MappingFlagType.content ? (
                                    <Typography.Text type={'secondary'}>
                                        This flag is about the problem's
                                        content, not its section. Review the
                                        problem (and the grader note, if any),
                                        make any edits there, then mark it
                                        reviewed. The section stays put unless
                                        you change it below.
                                    </Typography.Text>
                                ) : (
                                    <Typography.Text type={'secondary'}>
                                        Your ruling — keep or correct the
                                        section:
                                    </Typography.Text>
                                )}
                                <Select
                                    showSearch
                                    optionFilterProp={'label'}
                                    options={options}
                                    value={
                                        resolutions[detailDecision.id]?.path ??
                                        JSON.stringify(
                                            detailDecision.agentProposal
                                                .contentPath
                                        )
                                    }
                                    onChange={(path) =>
                                        setResolutions((value) => ({
                                            ...value,
                                            [detailDecision.id]: {
                                                path,
                                                keepNote:
                                                    value[detailDecision.id]
                                                        ?.keepNote ?? true,
                                            },
                                        }))
                                    }
                                />
                                {detailDecision.agentProposal.graderNote && (
                                    <Checkbox
                                        checked={
                                            resolutions[detailDecision.id]
                                                ?.keepNote ?? true
                                        }
                                        onChange={(event) =>
                                            setResolutions((value) => ({
                                                ...value,
                                                [detailDecision.id]: {
                                                    path:
                                                        value[detailDecision.id]
                                                            ?.path ??
                                                        JSON.stringify(
                                                            detailDecision
                                                                .agentProposal
                                                                .contentPath
                                                        ),
                                                    keepNote:
                                                        event.target.checked,
                                                },
                                            }))
                                        }
                                    >
                                        Keep the student-visible grader note
                                    </Checkbox>
                                )}
                                <Button
                                    type={'primary'}
                                    loading={resolvingId === detailDecision.id}
                                    onClick={() => void resolve(detailDecision)}
                                >
                                    {detailDecision.agentProposal.flagType ===
                                    MappingFlagType.content
                                        ? 'Mark reviewed'
                                        : 'Accept ruling'}
                                </Button>
                            </Space>
                        )}
                    </Space>
                )}
            </Drawer>
        </div>
    )
}
